import { PageHeader, useExport } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { MenuButton, Select, toast } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Eye } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PRODUCT_DISTRIBUTION_STATUSES,
  PRODUCT_STATUSES,
  useApi,
  type ProductDTO,
} from "../../api";
import { DISTRIBUTION_VARIANT, PRODUCT_STATUS_VARIANT } from "../../components/badges";
import { categoryFullName, EnumBadge, money, TwoLine } from "../../components/cells";
import { crudEditor, numberField, selectField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { ImportFileDialog } from "../artwork/dialogs";
import { LogicView } from "../../views";

/** A store or a client, whichever the price column is currently showing. */
interface PriceTarget {
  kind: "STORE" | "CLIENT";
  id: number;
  name: string;
}

function encodeTarget(target: PriceTarget): string {
  return `${target.kind}:${target.id}`;
}

/**
 * The product (variant) catalogue.
 *
 * The last column is deliberately contextual: pick a store or a client in its
 * filter and the column becomes that party's negotiated price, editable in
 * place. With nothing picked there is no price to show or write, which is why
 * the cell reads 0 and saving skips the price line entirely.
 */
export function ProductsPage() {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { start: startExport } = useExport();
  const allowed = useAllowed(LogicView.PRODUCT);
  const title = t("navigation.item.products", "Products");

  const [target, setTarget] = useState<PriceTarget | null>(null);
  const [importing, setImporting] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["product"],
    queryFn: () => api.products.findAll(),
    enabled: allowed,
    staleTime: 5 * 60_000,
  });

  const stores = useQuery({
    queryKey: ["store"],
    queryFn: () => api.stores.findAll(),
    enabled: allowed,
  });

  const clients = useQuery({
    queryKey: ["client"],
    queryFn: () => api.clients.findAll(),
    enabled: allowed,
  });

  /** Product id → price, for whichever party is selected. */
  const prices = useQuery({
    queryKey: ["product", "price", target?.kind, target?.id],
    queryFn: async () => {
      if (!target) return {} as Record<string, number>;
      const lines =
        target.kind === "STORE"
          ? await api.storePriceLines.getByStore(target.id)
          : await api.clientPriceLines.getByClient(target.id);
      return Object.fromEntries(
        Object.entries(lines).map(([productId, line]) => [productId, line.price]),
      );
    },
    enabled: allowed && target !== null,
  });

  const rows = data ?? [];
  const priceMap = prices.data ?? {};
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["product"] });
    await queryClient.invalidateQueries({ queryKey: ["product", "price"] });
  };

  const targets: PriceTarget[] = useMemo(
    () => [
      ...(stores.data ?? []).map((entry) => ({
        kind: "STORE" as const,
        id: entry.id,
        name: entry.name,
      })),
      ...(clients.data ?? []).map((entry) => ({
        kind: "CLIENT" as const,
        id: entry.id,
        name: entry.name,
      })),
    ],
    [clients.data, stores.data],
  );

  const cols = useMemo(() => {
    const c = defineColumns<ProductDTO>();
    return [
      c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
      c.custom(
        "status",
        (row) => row.status,
        (row) => <EnumBadge value={row.status} variants={PRODUCT_STATUS_VARIANT} />,
        { headerKey: "column.status", filter: "select", priority: 2 },
      ),
      c.custom(
        "distribution_status",
        (row) => row.distribution_status,
        (row) => <EnumBadge value={row.distribution_status} variants={DISTRIBUTION_VARIANT} />,
        { headerKey: "column.distribution-status", filter: "select", priority: 3 },
      ),
      c.custom(
        "category",
        (row) => categoryFullName(row.category),
        (row) => (
          <TwoLine primary={row.category?.family} secondary={categoryFullName(row.category)} />
        ),
        { headerKey: "column.category", filter: "select", priority: 2 },
      ),
      c.text("orientation", (row) => row.orientation, {
        headerKey: "column.orientation",
        filter: "select",
        priority: 4,
      }),
      c.text("ratio", (row) => row.ratio, {
        headerKey: "column.ratio",
        filter: "select",
        priority: 4,
      }),
      c.number("width", (row) => row.width, { headerKey: "column.width", priority: 5 }),
      c.number("height", (row) => row.height, { headerKey: "column.height", priority: 5 }),
      c.number("print_width", (row) => row.print_width, {
        headerKey: "column.print-width",
        priority: 5,
      }),
      c.number("print_height", (row) => row.print_height, {
        headerKey: "column.print-height",
        priority: 5,
      }),
      c.number("weight", (row) => row.weight, { headerKey: "column.weight", priority: 5 }),
      c.custom(
        "price",
        (row) => priceMap[String(row.id)] ?? 0,
        (row) => <span className="tabular-nums">{money(priceMap[String(row.id)] ?? 0)}</span>,
        { headerKey: "column.price.store-client", filter: "number", align: "right", priority: 3 },
      ),
    ];
  }, [priceMap]);

  const editor = useMemo(
    () =>
      crudEditor<ProductDTO>({
        client: api.products,
        refresh,
        t,
        canDelete: false,
        fields: {
          status: selectField(
            (row) => row.status,
            (row, value) => ({ ...row, status: value as ProductDTO["status"] }),
            PRODUCT_STATUSES,
          ),
          distribution_status: selectField(
            (row) => row.distribution_status,
            (row, value) => ({
              ...row,
              distribution_status: value as ProductDTO["distribution_status"],
            }),
            PRODUCT_DISTRIBUTION_STATUSES,
          ),
          width: numberField(
            (row) => row.width,
            (row, value) => ({ ...row, width: Number(value) }),
            true,
          ),
          height: numberField(
            (row) => row.height,
            (row, value) => ({ ...row, height: Number(value) }),
            true,
          ),
          print_width: numberField(
            (row) => row.print_width,
            (row, value) => ({ ...row, print_width: Number(value) }),
            true,
          ),
          print_height: numberField(
            (row) => row.print_height,
            (row, value) => ({ ...row, print_height: Number(value) }),
            true,
          ),
          weight: numberField(
            (row) => row.weight,
            (row, value) => ({ ...row, weight: Number(value) }),
            true,
          ),
          // The price cell writes through to the selected party's price line.
          price: numberField(
            (row) => priceMap[String(row.id)] ?? 0,
            (row, value) => {
              pendingPrice.current = value === "" ? 0 : Number(value);
              return row;
            },
          ),
        },
        validate: (row) => {
          const errors: Record<string, string> = {};
          const invalid = t("form.validation.field.invalid", "Value is invalid");
          (["width", "height", "print_width", "print_height", "weight"] as const).forEach((key) => {
            if (row[key] === null || row[key] === undefined || row[key] < 0) errors[key] = invalid;
          });
          return Object.keys(errors).length > 0 ? errors : null;
        },
        onSave: async (row) => {
          if (target) {
            const price = pendingPrice.current ?? priceMap[String(row.id)] ?? 0;
            if (target.kind === "STORE") {
              const lines = await api.storePriceLines.getByProduct(row.id);
              const existing = lines[String(target.id)];
              const line = existing ?? {
                id: 0,
                product_id: row.id,
                store_id: target.id,
                price: 0,
              };
              const next = { ...line, price };
              if (next.id === 0) await api.storePriceLines.create(next);
              else await api.storePriceLines.update(next);
            } else {
              const lines = await api.clientPriceLines.getByProduct(row.id);
              const existing = lines[String(target.id)];
              const line = existing ?? {
                id: 0,
                product_id: row.id,
                client_id: target.id,
                price: 0,
              };
              const next = { ...line, price };
              if (next.id === 0) await api.clientPriceLines.create(next);
              else await api.clientPriceLines.update(next);
            }
          }
          pendingPrice.current = null;
          await api.products.update(row);
        },
      }),
    [api, priceMap, t, target],
  );

  if (!allowed) return <AccessDenied title={title} />;
  if (error) return <ErrorState title={title} error={error} />;

  return (
    <>
      <PageHeader
        title={title}
        actions={
          <MenuButton
            label={t("menu.item.root.options", "Options")}
            items={[
              {
                label: t("menu.item.export.pricing", "Export pricing"),
                onClick: () =>
                  void startExport("pricing", () => api.products.documentPricing(false)),
              },
              {
                label: t("menu.item.export.pricing.open", "Export pricing (open)"),
                onClick: () => void startExport("pricing-open", () => api.products.documentPricing(true)),
              },
              {
                label: t("menu.item.import.pricing", "Import pricing"),
                onClick: () => setImporting(true),
              },
            ]}
          />
        }
      />

      <DataTable<ProductDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey="/product"
        loading={isLoading}
        editor={editor}
        toolbarExtra={
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[12px] text-ink-muted">
              {t("column.price.store-client", "Store/Client price")}
            </span>
            <Select
              className="h-8 w-56"
              value={target ? encodeTarget(target) : ""}
              placeholder={t("grid.filter", "Filter")}
              options={targets.map((entry) => ({
                value: encodeTarget(entry),
                label: `${entry.kind} - ${entry.name}`,
              }))}
              onChange={(event) => {
                const value = event.target.value;
                if (!value) {
                  setTarget(null);
                  return;
                }
                const [kind, id] = value.split(":");
                const found = targets.find(
                  (entry) => entry.kind === kind && String(entry.id) === id,
                );
                setTarget(found ?? null);
              }}
            />
          </div>
        }
        onRowClick={(row) => void navigate({ to: "/product/$id", params: { id: String(row.id) } })}
        rowActions={(row) => (
          <button
            type="button"
            title={t("button.edit", "Edit")}
            className="rounded p-1 text-ink-muted hover:bg-surface-sunk hover:text-ink"
            onClick={() => void navigate({ to: "/product/$id", params: { id: String(row.id) } })}
          >
            <Eye className="size-3.5" />
          </button>
        )}
      />

      <ImportFileDialog
        open={importing}
        onClose={() => setImporting(false)}
        title={t("dialog.import.title", "Import file")}
        accept=".xlsx,.xls,.csv"
        onImport={(file) => api.products.importPricing(file)}
        onImported={() => {
          void refresh();
          toast.success(t("notification.import.success", "File imported successfully"));
        }}
      />
    </>
  );
}

/**
 * The price typed into the inline editor, held outside React state.
 *
 * The editor's `set` must return a row of the same type, but the price does
 * not live on `ProductDTO` — it is a separate price-line row. Stashing it here
 * keeps the field editable without inventing a phantom DTO field.
 */
const pendingPrice: { current: number | null } = { current: null };
